import { useMemo } from "react";
import { createFileRoute, type LinkProps } from "@tanstack/react-router";
import z from "zod";

import { useIntegrations } from "@/hooks/use-integrations";
import { formatShortDate } from "@/utils";
import { InfoField, JsonDisplay } from "../../-components";

const BayesiaInferenceDetailPage = () => {
  const { id } = Route.useParams();

  const { data: bayesiaResult } = useIntegrations().getBayesiaResult(id);

  type InfoFieldType = { label: string; value: string | number } & LinkProps;

  const infoFields: InfoFieldType[] = useMemo(() => {
    const fields: InfoFieldType[] = [];
    if (bayesiaResult) {
      fields.push({ label: "ID", value: bayesiaResult.id });

      if (bayesiaResult.userId) {
        fields.push({
          label: "Profile ID",
          value: bayesiaResult.userId,
          to: "/profiles/$id/careplans",
          params: { id: bayesiaResult.userId },
        });
      }

      fields.push(
        { label: "Model", value: bayesiaResult.model },
        { label: "Model Version", value: bayesiaResult.modelVersion },
        {
          label: "Created At",
          value: formatShortDate(bayesiaResult.createdAt),
        },
      );
    }

    return fields;
  }, [bayesiaResult]);

  if (!bayesiaResult) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 rounded-xl bg-surface p-4">
        {infoFields.map((fields) => {
          return <InfoField key={fields.label} {...fields} />;
        })}
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-xl bg-surface p-4">
        <JsonDisplay data={bayesiaResult.input} label="Input" />

        <JsonDisplay data={bayesiaResult.output} label="Output" />
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_private/integrations/bayesia/inferences/$id/")({
  component: BayesiaInferenceDetailPage,
  params: {
    parse: (params: Record<string, string>) => {
      return { id: z.number().int().parse(Number(params.id)) };
    },
  },
});
